import _ from 'underscore';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  mergeClasses,
  makeStyles,
  tokens,
  Combobox,
  Option,
  useId,
  Field,
} from '@fluentui/react-components';
import { SearchFilled } from '@fluentui/react-icons';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';

interface SearchableSelectorProps {
  entities?: Array<{ name: string, id: string }>;
  onSelectEntity: (event: any, data: any) => void;
  label: string;
  placeholder: string;
  disabled?: boolean;
  errorMessage?: string;
  prefilledEntityId?: string;
  className?: string;
}

export const useSearchableSelectorStyles = makeStyles({
  entityName: {
    display: 'grid',
  },
  label: {
    lineHeight: tokens.lineHeightBase200,
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    '& label': {
      fontWeight: tokens.fontWeightRegular,
    },
  },
  combobox: {
    height: '30px',
    minWidth: 'fit-content',
  },
});

export const SearchableSelector: React.FC<SearchableSelectorProps> = ({
  entities,
  onSelectEntity,
  label,
  placeholder,
  disabled,
  errorMessage,
  prefilledEntityId,
  className = '',
}) => {
  const styles = useSearchableSelectorStyles();
  const labelId = useId('entity-selector');
  const i18n = useContext(I18nContext) as I18nModel;
  const [query, setQuery] = React.useState('');

  const updateQuery = useCallback((newQuery: any) => {
    setQuery(newQuery);
    const matchingEntity = entities?.find((entity) => entity.name === newQuery);
    onSelectEntity(null, { optionValue: matchingEntity?.id, optionText: matchingEntity?.name });
  }, [setQuery, onSelectEntity, entities]);

  useEffect(() => {
    // Prefill the entity name if the prefilledEntityId is provided, or if there is only one entity
    if (prefilledEntityId) {
      const matchingEntity = entities?.find((entity) => entity.id === prefilledEntityId);
      if (matchingEntity && matchingEntity.name) {
        updateQuery(matchingEntity.name);
      }
    } else if (entities?.length === 1) {
      updateQuery(entities[0].name);
    }
  }, [prefilledEntityId, entities, updateQuery]);

  const handleOptionSelect = useCallback((_e: any, data: any) => {
    if (data.optionValue && data.optionText) {
      updateQuery(data.optionText);
    }
  }, [updateQuery]);

  const handleChange = useCallback((e: any) => {
    updateQuery(e?.target?.value);
  }, [updateQuery]);

  const renderOptions = useCallback(() => {
    // If no query is provided, render all entities
    if (!query) {
      return entities?.map((entity) => (
        <Option className="searchable-selector-option" key={entity.id} value={entity.id}>{entity.name}</Option>
      ));
    }
    const matchingEntities = entities?.filter((entity) => entity.name.toLowerCase().includes(query.toLowerCase()));
    if (matchingEntities && matchingEntities.length > 0) {
      return matchingEntities.map((entity) => (
        <Option className="searchable-selector-option" key={entity.id} value={entity.id}>{entity.name}</Option>
      ));
    }
    return (
      // return non-selectable option if no results are found
      <Option className="searchable-selector-empty-option" key="empty-option" value="empty-option" disabled>
        {i18n.getString(_TL_('No results found'))}
      </Option>
    );
  }, [query, entities]);

  const selectedOptionId = useMemo(() => {
    const selectedIds = [];
    const selectedEntity = entities?.find((entity) => entity.name === query);
    if (selectedEntity && selectedEntity.id) {
      selectedIds.push(selectedEntity.id);
    }
    return selectedIds;
  }, [entities, query]);

  return (
    <div className={mergeClasses('entity-selector', className, styles.entityName)}>
      <Field
        size="small"
        label={label}
        className={styles.label}
        validationMessage={errorMessage}
        validationState={_.isEmpty(errorMessage) ? 'none' : 'error'}
      >
        <Combobox
          value={query ?? ''}
          freeform
          selectedOptions={selectedOptionId}
          aria-labelledby={labelId}
          placeholder={placeholder}
          expandIcon={<SearchFilled />}
          onOptionSelect={handleOptionSelect}
          onChange={handleChange}
          disabled={disabled}
          className={styles.combobox}
        >
          {renderOptions()}
        </Combobox>
      </Field>
    </div>
  );
};
